import React, { useState, useEffect } from "react";
import { UserContext } from "apps/common/authentication/common/constants";
import { getLastDigits } from "apps/portal/Portal/common/Settings/utils";
import {
  MFA_ATTEMPT_LIMIT_EXCEEDED_CODE,
  TWO_FA_FAILED_CODE,
} from "common/constants/errorCodes";
import { TwoFaType } from "common/types/mainEnums";
import {
  VERIFY_CODE_UNEXPECTED_ERROR_MESSAGE,
  UPGRADE_2FA_UNEXPECTED_ERROR_MESSAGE,
  SEND_CODE_UNEXPECTED_ERROR_MESSAGE,
} from "common/constants/copy";
import {
  useLoginMutation,
  useTwoStepAuthSendTokenToSmsForConfigurationMutation,
  useUpdateUserTwoFaMutation,
  useVerifyTwoFaCodeMutation,
} from "common/types/mainHooks";

import { type FormValue } from "apps/portal/Portal/common/VerifyTwoFAContainer/VerifyTwoFAForm";
import { toast } from "@jsq/ds/core";

import VerifyTwoFACard from "apps/portal/Portal/common/AccountActivation/VerifyTwoFACard";
import { setCSRFToken } from "common/utils/CSRFToken";
import { type UnauthenticatedArenaObjectType } from "apps/portal/Portal/common/types";
import { type Handle2FAInputs } from "apps/portal/Portal/common/AccountActivation/VerifyTwoFA";

const UNKNOWN_ERROR_MESSAGE = "An unexpected error occurred, please try again.";

export interface GpConfigureTwoFAProps {
  emailAddress: string;
  password: string;
  phoneNumber: string;
  countryCode: string;
  onSuccess?: () => void;
  onBackButtonClick?: () => void;
  arena?: UnauthenticatedArenaObjectType;
  splitScreen?: boolean;
  twoFaType?: TwoFaType;
  totpCode?: string;
}

/**
 * A container component used for GP 2FA Configuration
 * We only support rendering SMS 2FA for this workflow
 */
export const GPConfigureTwoFA = (props: GpConfigureTwoFAProps) => {
  const [twoFAInputErrorMessage, setTwoFAInputErrorMessage] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [shouldClearTwoFAInput, setShouldClearTwoFAInput] = useState(false);
  const [login] = useLoginMutation();
  const arenaDomain = window.location.host?.split(".")[0].toLowerCase();
  const lastDigits = getLastDigits(props.phoneNumber);

  const [twoStepAuthSendTokenToSmsForConfiguration] =
    useTwoStepAuthSendTokenToSmsForConfigurationMutation({
      onCompleted: (data) => {
        if (
          data?.twoStepAuthSendTokenToSmsForConfiguration?.__typename ===
          "MutationErrorResponse"
        ) {
          toast.error(data.twoStepAuthSendTokenToSmsForConfiguration.message);
        }
      },
      context: { unauthenticated: true },
      onError: () => toast.error(SEND_CODE_UNEXPECTED_ERROR_MESSAGE),
    });

  const [updateUserTwoFa] = useUpdateUserTwoFaMutation({
    context: { unauthenticated: true },
    onError: () => toast.error(UPGRADE_2FA_UNEXPECTED_ERROR_MESSAGE),
  });

  const [verifyTwoFaCode] = useVerifyTwoFaCodeMutation({
    context: { unauthenticated: true },
    onCompleted: (data) => {
      if (data?.verifyTwoFaCode?.__typename === "MutationErrorResponse") {
        const errorMessage = data.verifyTwoFaCode.message;
        if (
          data.verifyTwoFaCode.errorCode === TWO_FA_FAILED_CODE ||
          data.verifyTwoFaCode.errorCode === MFA_ATTEMPT_LIMIT_EXCEEDED_CODE
        ) {
          setTwoFAInputErrorMessage(errorMessage);
        } else {
          toast.error(errorMessage);
        }
      }
    },
    onError: () => toast.error(VERIFY_CODE_UNEXPECTED_ERROR_MESSAGE),
  });

  const sendCode = () => {
    twoStepAuthSendTokenToSmsForConfiguration({
      variables: {
        objectInput: {
          userContext: UserContext.Arena,
          emailAddress: props.emailAddress,
          password: props.password,
          arenaDomain,
          phoneNumber: props.phoneNumber,
          countryCode: props.countryCode,
        },
      },
    });
  };

  useEffect(() => {
    sendCode();
  }, []);

  const handleSubmit = async (values: FormValue, resetForm: () => void) => {
    setShouldClearTwoFAInput(false);
    setTwoFAInputErrorMessage("");

    const onError = () => {
      resetForm();
      setShouldClearTwoFAInput(true);
    };
    const verifyResult = await verifyTwoFaCode({
      variables: {
        objectInput: {
          userContext: UserContext.Arena,
          emailAddress: props.emailAddress,
          password: props.password,
          arenaDomain,
          verificationToken: values.verificationToken,
          totpCode: props.totpCode || "",
        },
      },
    });

    if (
      verifyResult.data?.verifyTwoFaCode?.__typename === "MutationSuccessResponse" &&
      verifyResult.data.verifyTwoFaCode.didSucceed
    ) {
      const updateResult = await updateUserTwoFa({
        variables: {
          objectInput: {
            emailAddress: props.emailAddress,
            password: props.password,
            countryCode: props.countryCode,
            phoneNumber: props.phoneNumber,
            verificationToken: values.verificationToken,
            totpCode: props.totpCode,
          },
        },
      });

      if (
        updateResult?.data?.updateUserTwoFa?.__typename === "MutationSuccessResponse" &&
        updateResult.data.updateUserTwoFa.didSucceed
      ) {
        // Proceed to log the user in
        setIsRedirecting(true);
        setShouldClearTwoFAInput(false);
        const { data } = await login({
          variables: {
            loginInput: {
              userContext: UserContext.Arena,
              arenaDomain,
              email: props.emailAddress,
              password: props.password,
              verificationCode: values.verificationToken,
            },
          },
          context: { unauthenticated: true },
        });

        if (!data?.login) {
          setTwoFAInputErrorMessage(UNKNOWN_ERROR_MESSAGE);
          setIsRedirecting(false);
          onError();
          return;
        }

        switch (data.login.__typename) {
          case "LoginObject":
            setCSRFToken(data.login.csrfToken);
            setIsRedirecting(true);
            props.onSuccess?.();
            break;
          default:
            setTwoFAInputErrorMessage(UNKNOWN_ERROR_MESSAGE);
            onError();
            setIsRedirecting(false);
            break;
        }
      }
    } else {
      onError();
    }
  };

  const handleInputChange = ({
    code,
    setFieldValue,
    submitForm,
    codeLength,
  }: Handle2FAInputs) => {
    setFieldValue("verificationToken", code);
    setTwoFAInputErrorMessage("");
    if (code.length === codeLength) {
      submitForm();
    }
  };

  return (
    <VerifyTwoFACard
      arena={props.arena}
      twoFAInputErrorMessage={twoFAInputErrorMessage}
      shouldClearTwoFAInput={shouldClearTwoFAInput}
      showCheckbox={false}
      showGoBackButton
      isComponentLoading={false}
      isButtonLoading={isRedirecting}
      handleSubmit={handleSubmit}
      handleInputChange={handleInputChange}
      sendCode={sendCode}
      twoFaSmsLastFourDigits={lastDigits}
      twoFaType={props.twoFaType ?? TwoFaType.Sms}
      onBackButtonClick={props.onBackButtonClick}
      splitScreen={props.splitScreen}
    />
  );
};

export default GPConfigureTwoFA;
